import { Helmet } from '@/lib/helmet-shim';

interface CrawlerTagsProps {
  /** Allow indexing (default: true) */
  index?: boolean;
  /** Allow following links (default: true) */
  follow?: boolean;
  /** Allow image indexing (default: true) */
  imageIndex?: boolean;
  /** Allow archiving (default: true) */
  archive?: boolean;
  /** Allow caching (default: true) */
  cache?: boolean;
  /** Maximum snippet length (-1 for unlimited) */
  maxSnippet?: number;
  /** Maximum image preview size */
  maxImagePreview?: 'none' | 'standard' | 'large';
  /** Maximum video preview in seconds (-1 for unlimited) */
  maxVideoPreview?: number;
  /** Prevent translation offers */
  noTranslate?: boolean;
  /** Prevent AI training on this content */
  noAiTraining?: boolean;
  /** Date after which not to show in search results */
  unavailableAfter?: string;
}

/**
 * CrawlerTags Component
 * 
 * Implements comprehensive crawler directives for fine-grained control
 * over how search engines index and display content.
 * 
 * @see https://developers.google.com/search/docs/crawling-indexing/robots-meta-tag
 * @see https://www.bing.com/webmasters/help/which-robots-metatags-does-bing-support-5198d240
 */
export const CrawlerTags = ({
  index = true,
  follow = true,
  maxSnippet = -1,
  maxImagePreview = 'large',
  maxVideoPreview = -1,
  noAiTraining = false,
}: CrawlerTagsProps) => {
  const robotsContent = [
    index ? 'index' : 'noindex',
    follow ? 'follow' : 'nofollow',
    `max-snippet:${maxSnippet}`,
    `max-image-preview:${maxImagePreview}`,
    `max-video-preview:${maxVideoPreview}`,
  ].join(', ');

  return (
    <Helmet>
      <meta name="robots" content={robotsContent} />
      {noAiTraining && (
        <meta name="robots" content="noai, noimageai" />
      )}
    </Helmet>
  );
};

export default CrawlerTags;
